// Checks made before anything is sent, the same rules as the manual admin forms and what the APIs would refuse.
// Each function returns a list of problems in plain words; an empty list means it may be saved.

const MIN_WEIGHT = 40;
const MAX_WEIGHT = 75;
const NON_RUNNER_GATE = 100;

const blank = (value) => value == null || String(value).trim() === "";
const realDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value)) && !Number.isNaN(new Date(value).getTime());
const inFuture = (value) => new Date(value).getTime() > Date.now();

export function validateLoad(date, raceNo) {
  const problems = [];
  if (!realDate(date)) problems.push("Please pick a date.");
  if (!/^\d{1,2}$/.test(String(raceNo)) || Number(raceNo) < 1) problems.push("Race number must be a number from 1.");
  return problems;
}

function duplicates(values) {
  const seen = new Set();
  const repeated = new Set();
  values.forEach((value) => (seen.has(value) ? repeated.add(value) : seen.add(value)));
  return [...repeated];
}

/** One runner, before it goes to /addracehorses. */
export function validateRunner(runner) {
  const name = runner.horse.era.name;
  const body = runner.entry.body;
  const problems = [];
  if (!body.HorseModelId) problems.push(`${name}: the horse is not in Saddle Battle yet`);
  if (runner.entry.equipmentMissing) problems.push(`${name}: equipment is not in Saddle Battle`);
  if (runner.nonRunner) return problems;
  if (blank(body.HorseNo)) problems.push(`${name}: no horse number`);
  if (!body.JockeyOnRace) problems.push(`${name}: no jockey (ERA gives jockeys with the declarations)`);
  const weight = Number(body.JockeyRaceWeight);
  if (!(weight >= MIN_WEIGHT && weight <= MAX_WEIGHT)) problems.push(`${name}: weight ${body.JockeyRaceWeight} kg looks wrong`);
  if (body.GateNo === NON_RUNNER_GATE) problems.push(`${name}: the draw is not out yet`);
  return problems;
}

/** The whole race, before /createrace. */
export function validateNewRace(comparison, extras) {
  const problems = [];
  // The Add Race form: what the admin sees is what is checked, ERA only filled it.
  const required = [
    ["Day", "race date"], ["StartTime", "start time"], ["RaceNumber", "race number"], ["MeetingCode", "meeting code"],
    ["HorseKindinRace", "horse kind"], ["RaceName", "race name"], ["RaceKind", "race kind"], ["RaceType", "race type"],
    ["MeetingType", "meeting type"], ["RaceCourse", "race course"], ["TrackLength", "track length"],
    ["TrackCondition", "track condition"], ["Currency", "currency"],
  ];
  required.forEach(([key, label]) => {
    if (blank(extras[key])) problems.push(`Pick the ${label}`);
  });
  if (!(Number(extras.totalPrize) > 0)) problems.push("Prize must be more than 0");
  if (blank(extras.DescriptionEn)) problems.push("Fill the description (English)");
  if (blank(extras.DescriptionAr)) problems.push("Fill the description (Arabic)");
  if (blank(extras.Sponsor)) problems.push("Pick the sponsor");
  comparison.runners.forEach((runner) => problems.push(...validateRunner(runner)));
  const active = comparison.runners.filter((r) => !r.nonRunner);
  duplicates(active.map((r) => r.horseNo).filter((no) => no != null)).forEach((no) => problems.push(`Horse number ${no} is used twice`));
  duplicates(active.map((r) => r.gate).filter((gate) => gate != null)).forEach((gate) => problems.push(`Gate ${gate} is used twice`));
  return problems;
}

/** The result, before /ResultCreationV2 or /EditRaceResultV3. */
export function validateResult(comparison, form) {
  const results = comparison.results;
  const problems = [];
  results.missing.forEach((name) => problems.push(`${name}: not on the Saddle Battle race card, or its position is not set up`));
  const notOnCard = comparison.runners.filter((r) => r.entry.state === "NEW").length;
  if (notOnCard) problems.push(`${notOnCard} runner(s) must be added to the race first`);
  if (!results.rows.some((row) => row.rank === 1)) problems.push("ERA's result has no winner");
  if (!results.winnerTime) problems.push("ERA's winning time cannot be read");
  if (results.rows.length !== comparison.runners.length) problems.push("ERA's result does not list every runner");
  duplicates(results.rows.map((row) => row.horseId).filter(Boolean)).forEach(() => problems.push("A horse is listed twice in the result"));
  if (blank(form.bestTurnOut)) problems.push("Pick the best turned out horse");
  if (!(Number(form.bestTurnPrice) >= 0) || blank(form.bestTurnPrice)) problems.push("Best turned out prize must be 0 or more");
  if (!blank(form.videoLink) && !/^https?:\/\/\S+$/i.test(form.videoLink)) problems.push("Video link must be a full link");
  return problems;
}

/** A new owner, trainer, jockey or horse, before its create API. */
export function validateRecord(kind, values, required, labels) {
  const problems = [];
  required.forEach((key) => {
    if (blank(values[key])) problems.push(`Fill: ${labels[key] || key}`);
  });
  if (!blank(values.shortCode) && !/^\d+$/.test(String(values.shortCode))) problems.push("Code must be a number");
  ["DOB", "TrainerLicenseDate", "JockeyLicenseDate", "RegistrationDate"].forEach((key) => {
    if (blank(values[key])) return;
    if (!realDate(values[key])) problems.push(`${labels[key] || key} is not a date`);
    else if (inFuture(values[key])) problems.push(`${labels[key] || key} cannot be in the future`);
  });
  if (kind === "jockey" && Number(values.MiniumumJockeyWeight) > Number(values.MaximumJockeyWeight)) {
    problems.push("Minimum weight is above maximum weight");
  }
  return problems;
}
